<ng-container>
	<form
		[ngSwitch]="currentStep"
		[formGroup]="form"
		(ngSubmit)="currentStep.nextAction()"
	>
		<div
			class="mb-4"
			*ngIf="currentStep !== _connectSteps[_connectStepsEmum.TERMS_OF_SERVICE]"
		>
			<ui-content-header title="Connect to Sync by Closer">
				<div actions class="flex gap-2 items-center">
					<p *ngIf="currentStep.stepNum" class="text-xs text-grey">
						Step {{ currentStep.stepNum }} of {{ numOfSteps }}
					</p>
					<ui-button icon="x" preset="ICON_DEFAULT" (click)="close()" />
				</div>
			</ui-content-header>
		</div>

		<!-- Welcome Step -->
		<section
			class="flex gap-4 mb-4"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.WELCOME]"
		>
			<div class="w-48 h-48 p-8 flex-shrink-0">
				<img
					src="assets/images/integrations/sync-logo.png"
					alt="Sync by Closer Logo"
				/>
			</div>
			<div class="flex flex-col gap-4">
				<div class="flex gap-1">
					<h3 class="h3">Welcome to Sync</h3>
				</div>
				<p class="p-lg">
					To complete Sync by Closer setup, follow these easy steps:
				</p>
				<closer-sync-auth-required-list></closer-sync-auth-required-list>
				<ui-note
					callToActionType="EXTERNAL"
					callToActionUrl="https://lso.ca/public-resources/finding-a-lawyer-or-paralegal/lawyer-and-paralegal-directory"
					callToActionLabel="View LSO Directory"
				>
					<p class="p-sm">
						An <strong>active fax number</strong> must be listed on your LSO
						profile to receive instructions from Sync-supported lenders. Click
						the link above to check your LSO listed fax number or add a new one
						to your LSO profile.
					</p>
				</ui-note>
			</div>
		</section>

		<!-- Send Email Step -->
		<section
			class="flex gap-4 mb-4"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.EMAIL]"
		>
			<div class="w-48 h-48 p-8 flex-shrink-0">
				<img
					src="assets/images/integrations/sync-logo.png"
					alt="Sync by Closer Logo"
				/>
			</div>
			<div class="flex flex-col gap-6">
				<h3 class="h3">Enter Your LSO Directory Email</h3>
				<p class="p-sm">
					First step: To validate your Sync account, Closer will send a
					verification code to the email address associated with your LSO
					directory profile.
				</p>

				<div
					class="flex flex-col gap-4"
					[ngClass]="{
						'opacity-40 pointer-events-none': validating,
					}"
				>
					<!-- TODO: NEED A UI COMPONENT OF THIS, this one has no tab-index	 -->
					<app-input
						[required]="true"
						label="LSO Email"
						formControlName="email"
						type="email"
					/>
					<div
						*ngIf="invalidEmail"
						class="w-full rounded-xl p-4 bg-orange-lightest border-2 border-orange-lighter flex items-center justify-center gap-2"
					>
						<app-icon
							[icon]="ICON.WARNING"
							size="lg"
							class="text-orange"
						></app-icon>
						<p class="p-sm">
							Please ensure the email provided is in the LSO directory. If you
							are still unable to connect, please contact support.
						</p>
					</div>
					<div class="flex items-center gap-4">
						<app-checkbox
							type="clear"
							formControlName="termsAndConditions"
						></app-checkbox>
						<p class="p-sm">
							I agree to the
							<button
								class="text-tealGreen-600 cursor-pointer hover:underline duration-300"
								(click)="termsOfServiceClick()"
							>
								terms of service
							</button>
							for Sync by Closer.
						</p>
					</div>
				</div>

				<div class="flex gap-2 justify-left items-center p-xs">
					<p>Issues verifying with your LSO Email or need another option?</p>
					<a
						class="text-center hover:underline cursor-pointer text-green lso-email-bot"
						attr.data-intercom-target="lso-email-intercom-trigger"
					>
						Chat with us!
					</a>
				</div>
			</div>
		</section>

		<section *ngSwitchCase="_connectSteps[_connectStepsEmum.TERMS_OF_SERVICE]">
			<app-terms-of-service></app-terms-of-service>
		</section>

		<!-- Enter Code Step -->
		<section
			class="flex gap-4 mb-4"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.VERIFICATION_CODE]"
		>
			<div class="w-48 h-48 p-8 flex-shrink-0">
				<img
					src="assets/images/integrations/sync-logo.png"
					alt="Sync by Closer Logo"
				/>
			</div>
			<div class="flex flex-col gap-6">
				<h3 class="h3">Enter Your Verification Code</h3>
				<p class="p-sm">
					Next step: Check your inbox! Open the mail you have received from Sync
					by Closer and add the verification code in that email below.
				</p>
				<div
					[ngClass]="{
						'opacity-40 pointer-events-none': validating,
					}"
				>
					<app-input
						[required]="true"
						label="Verification Code"
						formControlName="pin"
						placeholder="Enter your verification code"
					/>
				</div>
				<ui-note
					title="Don't See The Email?"
					callToActionType="BUTTON"
					callToActionLabel="Resend Email"
					(callToActionClick)="validateEmailClick()"
				>
					<!-- <ui-button [style]="'ghost'" label="Resend Email" (click)="validateEmailClick()"></ui-button> -->
					<p class="p-sm">
						Make sure to check your junk/spam folder or take a look at the
						<a
							class="text-blue-highlight hover:underline"
							externalNavInterceptor
							href="https://lso.ca/public-resources/finding-a-lawyer-or-paralegal/lawyer-and-paralegal-directory"
							target="_blank"
							>LSO directory</a
						>
						to check the email you have listed.
					</p>
				</ui-note>
			</div>
		</section>

		<!-- Add Signature Step -->
		<section
			class="flex gap-4 mb-4"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.SIGNATURE]"
		>
			<div class="w-48 h-48 p-8 flex-shrink-0">
				<img
					src="assets/images/integrations/sync-logo.png"
					alt="Sync by Closer Logo"
				/>
			</div>
			<div class="flex flex-col gap-6">
				<div class="flex flex-col gap-4">
					<h3 class="h3">Add Your Digital Signature</h3>
					<p class="text-sm">
						Next, draw a digital signature to be added to applicable lender
						forms, including Request for Funds and Final Reports.
					</p>
				</div>
				<div class="flex flex-col gap-6">
					<app-signature-pad
						card-content
						[(signatureImg)]="signature"
					></app-signature-pad>
					<div>
						<h4 class="h4">Give Your Consent</h4>
						<div class="flex flex-col gap-2">
							<app-checkbox
								name="signatureConsent"
								type="clear"
								formControlName="signatureConsent"
								required
								class="p-sm"
							>
								I authorize LawLabs to utilize my captured signature on all
								mortgage forms, including Request for Funds and Final Reports.
							</app-checkbox>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Set Pin Step -->
		<section
			class="flex gap-4 mb-4 items-center"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.PIN]"
		>
			<div class="basis-2/5 h-full p-2 pb-6 flex-shrink-0">
				<img
					src="assets/images/integrations/sync-pin.svg"
					class="w-full h-full object-cover"
					alt="Sync by Closer Security PIN"
				/>
			</div>
			<div class="flex flex-col gap-4 py-6 basis-3/5">
				<h2 class="h2">Add Your Security PIN</h2>
				<p class="p-sm">
					Create a secure 4-digit PIN to be used to apply your digital signature
					to all lender forms, including Request for Funds and Final Reports.
				</p>
				<div
					class="flex flex-col gap-4"
					formGroupName="accessPinForm"
					[ngClass]="{
						'opacity-40 pointer-events-none': validating,
					}"
				>
					<app-input-wrapper>
						<app-input
							name="pin"
							formControlName="accessPin"
							type="password"
							placeholder="Create a PIN"
							label="Enter PIN"
							[exactLength]="4"
							appDigitsOnly
							required
						></app-input>
					</app-input-wrapper>
					<app-input-wrapper>
						<app-input
							name="confirmPin"
							formControlName="confirmAccessPin"
							[ngModel]=""
							type="password"
							placeholder="Re-enter your PIN"
							label="Re-enter PIN"
							[exactLength]="4"
							appDigitsOnly
							required
						></app-input>
					</app-input-wrapper>
				</div>
			</div>
		</section>

		<!-- Lender Consent Step -->
		<section
			class="flex flex-col gap-4 py-6"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.LENDER_CONSENT]"
		>
			<div class="flex gap-6">
				<div class="w-48 h-48 p-8 flex-shrink-0">
					<img
						src="assets/images/integrations/sync-logo.png"
						alt="Sync by Closer Logo"
					/>
				</div>
				<div>
					<h2 class="h2">Review Supported Lenders</h2>
					<p class="p-sm mt-2">
						Sync facilitates the mortgage processing for
						{{ syncInstitutions.length }} of your lenders, making it easier for
						you take charge over your instructions. Here’s the list:
					</p>
				</div>
			</div>
			<div
				class="rounded-lg border grid grid-cols-2 grid-rows-5 gap-x-6 gap-y-2 py-6 px-4"
			>
				<div
					class="flex items-center border rounded-lg justify-between gap-6 px-4 py-2"
					*ngFor="let lender of closerSyncLenders"
				>
					<div>
						<img
							class="max-w-[80px]"
							src="assets/images/banks/{{ lender.imgUrl }}"
						/>
					</div>
					<div class="flex-1">{{ lender.displayName || lender.label }}</div>
				</div>
			</div>
			<app-checkbox
				class="p-sm"
				name="lenderConsent"
				type="clear"
				formControlName="lenderConsent"
				required
			>
				I authorize representatives of LawLabs to verify the receipt of my
				submitted mortgage forms, including Request for Funds and Final Reports.
			</app-checkbox>
			<app-checkbox
				name="signatureConsent"
				type="clear"
				formControlName="lsoConsent"
				required
				class="p-sm"
			>
				I authorize LawLabs to use my LSO Fax number
				<span *ngIf="loggedInBusinessUser?.lsoFaxNumber"
					>({{ loggedInBusinessUser?.lsoFaxNumber }})</span
				>
				for the purpose of transmitting lender forms.
			</app-checkbox>
		</section>

		<!-- Congrats Completed -->
		<section
			class="flex gap-4 mb-4"
			*ngSwitchCase="_connectSteps[_connectStepsEmum.CONGRATS_CONNECTED]"
		>
			<div class="w-48 h-48 p-8 flex-shrink-0">
				<img
					src="assets/images/integrations/sync-logo.png"
					alt="Sync by Closer Logo"
				/>
			</div>
			<app-closer-sync-congrats-connected></app-closer-sync-congrats-connected>
		</section>

		<div class="flex justify-end items-center gap-2 mt-auto">
			<ui-button
				*ngIf="currentStep.backAction"
				[label]="currentStep.backActionText ?? 'Back'"
				(click)="currentStep.backAction()"
				[style]="'outline'"
				color="gray"
			/>
			<ui-button
				*ngIf="currentStep.nextAction"
				(click)="currentStep.nextAction()"
				[label]="currentStep.nextActionText ?? 'Next'"
				[disabled]="disableNextButton"
				[loading]="validating"
			/>
		</div>
	</form>

	<!-- Verifying Pin Status Step -->
	<!-- <section class="flex flex-col items-center justify-center gap-12 min-h-[320px]" *ngIf="validating">
		<p class="text-center">{{currentStep.loadingText}}</p>
		<div class="mb-12">
			<img src="/assets/images/integrations/sync-icon.png" class="w-20 animate-reverse-spin" />
		</div>
	</section> -->
</ng-container>
